import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import LazyVideo from '@cartwhl/ui/atoms/LazyVideo';

import type { MarketingMedia as Media } from './marketing-media';

interface MarketingMediaProps {
  className: string;
  contained?: boolean;
  dataPart?: string;
  eager?: boolean;
  /** Load the still immediately without eagerly mounting video sources. */
  eagerPoster?: boolean;
  media: Media;
  retainAfterLoaded?: boolean;
  /** null delays mounting until a client-only start time has been chosen. */
  startTime?: number | null;
}

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
const maxAutomaticRetries = 4;
const retryDelayMs = 500;

interface SaveDataConnection {
  saveData?: boolean;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

function prefersStillMedia() {
  return (
    window.matchMedia(reducedMotionQuery).matches ||
    (navigator as Navigator & { connection?: SaveDataConnection }).connection
      ?.saveData === true
  );
}

export default function MarketingMedia({
  className,
  contained = false,
  dataPart,
  eager = false,
  eagerPoster = false,
  media,
  retainAfterLoaded = true,
  startTime,
}: MarketingMediaProps) {
  if (media.type === 'empty') {
    return (
      <div
        className={`bg-tertiary ${className}`}
        role="img"
        aria-label={media.description}
        data-model-hero-part={dataPart}
      />
    );
  }

  if (media.type === 'image') {
    return (
      <div
        className={`bg-tertiary overflow-hidden ${className}`}
        data-model-hero-part={dataPart}
      >
        <img
          alt={media.description}
          className="h-full w-full object-cover"
          decoding="async"
          loading={eager ? 'eager' : 'lazy'}
          src={media.src}
        />
      </div>
    );
  }

  return (
    <MarketingVideo
      key={media.src}
      className={className}
      contained={contained}
      dataPart={dataPart}
      eager={eager}
      eagerPoster={eagerPoster}
      media={media}
      retainAfterLoaded={retainAfterLoaded}
      startTime={startTime}
    />
  );
}

interface MarketingVideoProps extends Omit<MarketingMediaProps, 'media'> {
  media: Extract<Media, { type: 'video' }>;
}

function MarketingVideo({
  className,
  contained = false,
  dataPart,
  eager = false,
  eagerPoster = false,
  media,
  retainAfterLoaded = true,
  startTime,
}: MarketingVideoProps) {
  const [stillPreferred, setStillPreferred] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [manualPlay, setManualPlay] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [playBlocked, setPlayBlocked] = useState(false);
  const [retry, setRetry] = useState({ key: 0, failures: 0 });
  const [retriesExhausted, setRetriesExhausted] = useState(false);
  const [manualRecovery, setManualRecovery] = useState(false);
  const [vp9Only, setVp9Only] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const manualPlayRef = useRef<(() => void) | null>(null);
  const failed = useRef(false);
  const vp9Fallback = useRef(false);
  const preferenceRef = useRef<boolean | null>(null);
  const sessionRef = useRef(0);
  const session = sessionRef.current;
  const isCurrentSession = () => session === sessionRef.current;

  const cancelRetry = useCallback(() => {
    if (retryTimer.current !== null) clearTimeout(retryTimer.current);
    retryTimer.current = null;
  }, []);

  useEffect(() => cancelRetry, [cancelRetry]);

  useEffect(() => {
    failed.current = false;
  }, [useFallback]);

  const handleError = (details?: { code?: number; currentSrc: string }) => {
    if (!isCurrentSession() || failed.current) return;
    failed.current = true;
    setHasPlayed(false);
    const av1 = media.sources?.[0];
    if (
      details?.code === 3 &&
      av1?.type.includes('av01') &&
      details.currentSrc === new URL(av1.src, document.baseURI).href &&
      media.sources?.some((source) => source.type.includes('vp9'))
    ) {
      vp9Fallback.current = true;
    }
    if (manualPlay) {
      // A replacement manual player cannot start without another user gesture.
      // Keep the selected fallback for the next click, without mounting it now.
      cancelRetry();
      sessionRef.current += 1;
      if (media.fallbackSrc && !useFallback) setUseFallback(true);
      setVp9Only(vp9Fallback.current);
      setManualRecovery(true);
      setManualPlay(false);
      setPlayBlocked(true);
      return;
    }
    setPlayBlocked(false);
    if (media.fallbackSrc && !useFallback) {
      sessionRef.current += 1;
      setUseFallback(true);
      setRetry(({ key }) => ({ key: key + 1, failures: 0 }));
      return;
    }
    if (retry.failures >= maxAutomaticRetries) {
      setRetriesExhausted(true);
      setManualPlay(false);
      setHasPlayed(false);
      return;
    }
    retryTimer.current = setTimeout(
      () => {
        retryTimer.current = null;
        sessionRef.current += 1;
        failed.current = false;
        setVp9Only(vp9Fallback.current);
        setRetry(({ key, failures }) => ({
          key: key + 1,
          failures: failures + 1,
        }));
      },
      retryDelayMs * (retry.failures + 1),
    );
  };

  const revealFrame = () => {
    if (isCurrentSession() && !failed.current && (!playBlocked || manualPlay))
      setHasPlayed(true);
  };

  const restorePoster = () => {
    cancelRetry();
    surfaceRef.current?.querySelector('video')?.pause();
    setManualPlay(false);
    setHasPlayed(false);
    // For an autoplay-blocked session, keep the player in manual mode.
    if (!stillPreferred) setPlayBlocked(true);
  };

  const startManually = () => {
    cancelRetry();
    failed.current = false;
    if (stillPreferred || retriesExhausted || manualRecovery)
      sessionRef.current += 1;
    // Commit the player and its manual play callback inside the user gesture.
    flushSync(() => {
      if (stillPreferred || retriesExhausted || manualRecovery) {
        setVp9Only(vp9Fallback.current);
        setRetriesExhausted(false);
        setManualRecovery(false);
        setRetry(({ key }) => ({ key: key + 1, failures: 0 }));
      }
      setManualPlay(true);
      setHasPlayed(false);
    });
    manualPlayRef.current?.();
  };

  useEffect(() => {
    const query = window.matchMedia(reducedMotionQuery);
    const connection = (
      navigator as Navigator & { connection?: SaveDataConnection }
    ).connection;
    const updatePreference = () => {
      const still = prefersStillMedia();
      if (preferenceRef.current !== still) {
        preferenceRef.current = still;
        sessionRef.current += 1;
      }
      setStillPreferred(still);
      setManualPlay(false);
      if (still) {
        cancelRetry();
        setHasPlayed(false);
        setPlayBlocked(false);
        if (failed.current) {
          failed.current = false;
          setVp9Only(vp9Fallback.current);
          setRetriesExhausted(false);
          setRetry(({ key }) => ({ key: key + 1, failures: 0 }));
        }
      }
      setPreferencesReady(true);
    };
    updatePreference();
    query.addEventListener('change', updatePreference);
    connection?.addEventListener?.('change', updatePreference);
    return () => {
      query.removeEventListener('change', updatePreference);
      connection?.removeEventListener?.('change', updatePreference);
    };
  }, [cancelRetry]);

  return (
    <div
      ref={surfaceRef}
      className={`bg-tertiary relative overflow-hidden ${className}`}
      data-model-hero-part={dataPart}
    >
      {media.poster ? (
        <img
          className={`absolute inset-0 h-full w-full ${contained ? 'object-contain' : 'object-cover'}`}
          src={media.poster}
          alt={media.description}
          loading={eagerPoster || eager ? 'eager' : 'lazy'}
          fetchPriority={eagerPoster ? 'high' : undefined}
          decoding="async"
        />
      ) : (
        <div
          className="bg-tertiary absolute inset-0"
          role="img"
          aria-label={media.description}
        />
      )}
      {preferencesReady &&
        startTime !== null &&
        !retriesExhausted &&
        !manualRecovery &&
        (!stillPreferred || manualPlay) && (
          <LazyVideo
            key={retry.key}
            src={useFallback ? media.fallbackSrc! : media.src}
            sources={
              useFallback
                ? undefined
                : vp9Only
                  ? media.sources?.filter(
                      (source) =>
                        source.type.includes('vp9') ||
                        source.type.includes('mp4'),
                    )
                  : media.sources
            }
            dimOnError={false}
            contained={contained}
            mode={
              stillPreferred || playBlocked || manualPlay
                ? 'manual'
                : 'autoplay'
            }
            loop={!stillPreferred}
            className={`absolute inset-0 h-full w-full rounded-[inherit] ${hasPlayed ? '' : 'opacity-0'}`}
            eager={eager || manualPlay}
            retainAfterLoaded={retainAfterLoaded}
            startTime={startTime}
            // Give approaching cards time to buffer without loading a full viewport ahead.
            rootMargin="500px"
            threshold={0}
            inViewThreshold={0.001}
            strictViewportPlayback
            preload="auto"
            poster={media.poster}
            manualPlayRef={manualPlayRef}
            onFirstFrame={() => {
              if (!isCurrentSession()) return;
              revealFrame();
              setPlayBlocked(false);
            }}
            onLoadedData={revealFrame}
            onPlayRejected={() => {
              if (!isCurrentSession()) return;
              setPlayBlocked(true);
              setManualPlay(false);
              setHasPlayed(false);
            }}
            onStartStalled={() => {
              if (!isCurrentSession()) return;
              if (!manualPlay) {
                setPlayBlocked(true);
                setHasPlayed(false);
              }
            }}
            onEnded={() => {
              if (!isCurrentSession()) return;
              if (stillPreferred) {
                setManualPlay(false);
                setHasPlayed(false);
                setPlayBlocked(false);
              }
            }}
            onInViewChange={(inView) => {
              if (!isCurrentSession()) return;
              if (!inView && !retainAfterLoaded) setHasPlayed(false);
            }}
            onError={handleError}
          />
        )}
      {preferencesReady &&
        (stillPreferred ||
          playBlocked ||
          retriesExhausted ||
          manualRecovery ||
          manualPlay) && (
          <button
            type="button"
            className="absolute inset-0 z-20 flex items-center justify-center text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-green-50"
            onClick={() => {
              if (manualPlay) {
                restorePoster();
                return;
              }
              startManually();
            }}
            aria-label={`${manualPlay ? 'Stop' : 'Play'} ${media.description}`}
          >
            {!manualPlay && (
              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                className="h-12 w-12 fill-current drop-shadow-lg"
              >
                <path d="M5 3.5a1 1 0 0 1 1.5-.86l10 6.5a1 1 0 0 1 0 1.72l-10 6.5A1 1 0 0 1 5 16.5z" />
              </svg>
            )}
          </button>
        )}
    </div>
  );
}
